import React, { useState, useEffect, useRef, useCallback } from 'react';
import './allCarListed.css';
import { useNavigate } from 'react-router-dom';
import { Box, MenuItem, Typography, Pagination, Select } from '@mui/material';
import { styled } from '@mui/system';
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// --- BASE API URL ---
const BASE_URL = 'https://car.amar.co.jp';

// Spinning Logo Styled Component
const SpinningLogo = styled("img")(({ theme }) => ({
  width: "150px",
  height: "150px",
}));

// ============ SEARCH STYLED COMPONENTS ============

const SearchInputWrapper = styled("div")(() => ({
  position: "relative",
  width: "100%",
}));

const SuggestionsDropdown = styled("div")(() => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  background: "white",
  border: "1px solid #ddd",
  borderTop: "none",
  borderRadius: "0 0 4px 4px",
  maxHeight: "400px",
  overflowY: "auto",
  zIndex: 1000,
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  marginTop: "-1px",

  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#d0d0d0",
    borderRadius: "10px",

    "&:hover": {
      background: "#b0b0b0",
    },
  },
}));

const SuggestionsList = styled("ul")(() => ({
  listStyle: "none",
  padding: 0,
  margin: 0,
}));

const SuggestionItem = styled("li")(() => ({
  display: "flex",
  alignItems: "center",
  padding: "12px 16px",
  cursor: "pointer",
  borderBottom: "1px solid #f0f0f0",
  transition: "background-color 0.2s ease",

  "&:hover": {
    backgroundColor: "#f9f9f9",
  },

  "&:last-child": {
    borderBottom: "none",
  },

  "&:focus": {
    outline: "2px solid #d32f2f",
    outlineOffset: "-1px",
  },
}));

const SuggestionImage = styled("div")(() => ({
  width: "60px",
  height: "60px",
  marginRight: "12px",
  borderRadius: "4px",
  overflow: "hidden",
  flexShrink: 0,
  backgroundColor: "#f5f5f5",

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
}));

const SuggestionInfo = styled("div")(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",

  "& h4": {
    margin: 0,
    fontSize: "14px",
    fontWeight: 600,
    color: "#333",
    lineHeight: 1.4,
  },

  "& p": {
    margin: "4px 0 0 0",
    fontSize: "12px",
    color: "#666",
    lineHeight: 1.3,
  },
}));

const SuggestionsLoading = styled("div")(() => ({
  padding: "20px 16px",
  textAlign: "center",
  color: "#999",
  fontSize: "13px",
  fontWeight: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",

  "&::before": {
    content: '""',
    display: "inline-block",
    width: "14px",
    height: "14px",
    border: "2px solid #f3f3f3",
    borderTop: "2px solid #d32f2f",
    borderRadius: "50%",
    animation: "spin 0.6s linear infinite",
  },

  "@keyframes spin": {
    "0%": {
      transform: "rotate(0deg)",
    },
    "100%": {
      transform: "rotate(360deg)",
    },
  },
}));

const SuggestionsEmpty = styled("div")(() => ({
  padding: "20px 16px",
  textAlign: "center",
  color: "#999",
  fontSize: "13px",
  fontWeight: 500,
  background: "linear-gradient(to right, #fff 0%, #f9f9f9 50%, #fff 100%)",
}));

const SearchButton = styled("button")(() => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "6px 10px",
  marginRight: "4px",
  transition: "transform 0.2s ease",

  "&:hover": {
    transform: "scale(1.1)",
  },

  "&:active": {
    transform: "scale(0.95)",
  },

  "&:focus": {
    outline: "2px solid #d32f2f",
    outlineOffset: "2px",
  },
}));

// ================================================

const EditCarModal = ({
  carModelId,
  onClose,
  priceRanges,
  engineRanges,
  seatRanges,
  mileageRanges,
  driveTypes,
  allAccessories,
  setAllAccessories,
  refreshCarList
}) => {
  const [fuelTypes, setFuelTypes] = useState([]);
  const [transmissions, setTransmissions] = useState([]);
  const [steeringOptions, setSteeringOptions] = useState([]);
  const [colors, setColors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [originalPrice, setOriginalPrice] = useState(carModelId?.carPriceValue || 0);

  const [loadingFuelTypes, setLoadingFuelTypes] = useState(false);
  const [loadingTransmissions, setLoadingTransmissions] = useState(false);
  const [loadingSteering, setLoadingSteering] = useState(false);
  const [loadingColors, setLoadingColors] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [errors, setErrors] = useState({});

  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const brandDropdownRef = useRef(null);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [portFeeConfigs, setPortFeeConfigs] = useState([]);
  const [loadingPortFeeConfigs, setLoadingPortFeeConfigs] = useState(false);

  const getAllUniqueFees = () => {
    const s = new Set();
    (portFeeConfigs || []).forEach(cfg => {
      (cfg.feeConfigurationList || []).forEach(f => s.add(f.trim()));
    });
    return Array.from(s);
  };

  const formatFeeLabel = (feeType) => feeType.replace(/_/g, ' ');

  const getIndices = (optionName, feeType) => {
    const optionIndex = (formData.shippingFeeConfig || []).findIndex(
      o => o.shippingOption === optionName
    );
    const feeIndex =
      optionIndex > -1
        ? (formData.shippingFeeConfig[optionIndex].feeConfigs || []).findIndex(
          f => (f.feeConfig || '').trim() === feeType
        )
        : -1;
    return { optionIndex, feeIndex };
  };

  const fetchPortFeeConfigs = async () => {
    setLoadingPortFeeConfigs(true);
    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/authorization/admin-shipment-panel/get-all-port-fee-config',
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const data = await response.json();
      setPortFeeConfigs(data);

      if (!formData.shippingFeeConfig || formData.shippingFeeConfig.length === 0) {
        const initialShippingConfig = data.map(config => ({
          shippingOption: config.shippingOptionName,
          feeConfigs: config.feeConfigurationList.map(fee => ({
            feeConfig: fee.trim(),
            feeConfigAmount: 0
          }))
        }));

        setFormData(prev => ({
          ...prev,
          shippingFeeConfig: initialShippingConfig
        }));
      } else {
        const mergedConfig = data.map(apiConfig => {
          const existingConfig = formData.shippingFeeConfig.find(
            f => f.shippingOption === apiConfig.shippingOptionName
          );

          if (existingConfig) {
            const existingFees = existingConfig.feeConfigs || [];
            const apiFees = apiConfig.feeConfigurationList || [];

            const existingFeeMap = new Map(
              existingFees.map(fee => [fee.feeConfig.trim(), fee.feeConfigAmount])
            );

            const mergedFees = apiFees.map(fee => ({
              feeConfig: fee.trim(),
              feeConfigAmount: existingFeeMap.get(fee.trim()) || 0
            }));

            return {
              shippingOption: apiConfig.shippingOptionName,
              feeConfigs: mergedFees
            };
          }

          return {
            shippingOption: apiConfig.shippingOptionName,
            feeConfigs: apiConfig.feeConfigurationList.map(fee => ({
              feeConfig: fee.trim(),
              feeConfigAmount: 0
            }))
          };
        });

        setFormData(prev => ({
          ...prev,
          shippingFeeConfig: mergedConfig
        }));
      }
    } catch (error) {
      console.error('Error fetching port fee configs:', error);
      showDialog('Error', 'Failed to load shipping options');
    } finally {
      setLoadingPortFeeConfigs(false);
    }
  };

  const [dialog, setDialog] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });

  const showDialog = (title, message, onConfirm = null, onCancel = null) => {
    setDialog({
      show: true,
      title,
      message,
      onConfirm,
      onCancel
    });
  };

  const fetchCategories = async () => {
    if (categories.length > 0) return;

    setLoadingCategories(true);
    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/master/car-catgories/get-all-categories',
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const data = await response.json();
      if (data.data) {
        setCategories(data.data.map(item => item.categoriesName));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showDialog('Error', 'Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const filteredBrands = brands.filter(brand =>
    brand.toLowerCase().includes(brandSearchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(event.target)) {
        setBrandDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleBrandSelect = (brand) => {
    setFormData(prev => ({ ...prev, carBrand: brand }));
    setBrandDropdownOpen(false);
    setBrandSearchTerm('');
  };

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    fetchFuelTypes();
    fetchTransmissions();
    fetchSteeringOptions();
    fetchColors();
    fetchPortFeeConfigs();
  }, []);

  const fetchBrands = async () => {
    if (brands.length > 0) return;

    setLoadingBrands(true);
    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/master/car-brand/get-brand-list',
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const data = await response.json();
      if (data.carBrandList) {
        setBrands(data.carBrandList.map(item => item.carBrandName));
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      showDialog('Error', 'Failed to load brands');
    } finally {
      setLoadingBrands(false);
    }
  };

  const fetchFuelTypes = async () => {
    if (fuelTypes.length > 0) return;

    setLoadingFuelTypes(true);
    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/dashboard/car-fuel-type/get-all-fuel-type',
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const data = await response.json();
      setFuelTypes(data.map(item => item.carFuelType));
    } catch (error) {
      console.error('Error fetching fuel types:', error);
      showDialog('Error', 'Failed to load fuel type');
    } finally {
      setLoadingFuelTypes(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      'carCategories', 'carBrand', 'carModelName', 'carPrice', 'carYear',
      'carReferenceNumber', 'carRegistrationDate', 'carManufactureDate',
      'carModelCode', 'carGrade', 'carChassisNumber', 'carEngineCC',
      'carEngineCode', 'carFuelType', 'carTransmission', 'carSteering',
      'carSeats', 'carDoors', 'carMileage', 'carColor', 'carDrive',
      'carLocation', 'carLength', 'carHeight', 'carWidth', 'airConditioning',
      'modelDescription', 'weight'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return Object.keys(newErrors)[0];
    }

    return null;
  };

  const fetchTransmissions = async () => {
    if (transmissions.length > 0) return;

    setLoadingTransmissions(true);
    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/dashboard/car-transmission/get-all-transmission-type',
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const data = await response.json();
      setTransmissions(data.map(item => item.carTransmission));
    } catch (error) {
      console.error('Error fetching transmissions:', error);
      showDialog('Error', 'Failed to load transmissions');
    } finally {
      setLoadingTransmissions(false);
    }
  };

  const fetchSteeringOptions = async () => {
    if (steeringOptions.length > 0) return;

    setLoadingSteering(true);
    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/dashboard/car-steering/get-all-steering-type',
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const data = await response.json();
      setSteeringOptions(data.map(item => item.carSteering));
    } catch (error) {
      console.error('Error fetching steering options:', error);
      showDialog('Error', 'Failed to load steering options');
    } finally {
      setLoadingSteering(false);
    }
  };

  const fetchColors = async () => {
    if (colors.length > 0) return;

    setLoadingColors(true);
    try {
      const response = await fetch(
        'https://car.amar.co.jp/api/dashboard/car-color/get-all-color',
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );
      const data = await response.json();
      setColors(data.map(item => item.carColor));
    } catch (error) {
      console.error('Error fetching colors:', error);
      showDialog('Error', 'Failed to load colors');
    } finally {
      setLoadingColors(false);
    }
  };

  const getPriceRange = (price) => {
    if (!price) return '';
    const priceNum = parseFloat(price);
    if (priceNum <= 1000) return '$0-$1000';
    if (priceNum <= 2000) return '$1001-$2000';
    if (priceNum <= 3000) return '$2001-$3000';
    if (priceNum <= 4000) return '$3001-$4000';
    if (priceNum <= 5000) return '$4001-$5000';
    if (priceNum <= 6000) return '$5001-$6000';
    if (priceNum <= 7000) return '$6001-$7000';
    if (priceNum <= 8000) return '$7001-$8000';
    if (priceNum <= 9000) return '$8001-$9000';
    if (priceNum <= 10000) return '$9001-$10000';
    return '$10001_ABOVE';
  };

  const getEngineCCRange = (cc) => {
    if (!cc) return '';
    const ccNum = parseInt(cc);
    if (ccNum <= 500) return '0CC-500CC';
    if (ccNum <= 1000) return '501CC-1000CC';
    if (ccNum <= 1500) return '1001CC-1500CC';
    if (ccNum <= 2000) return '1501CC-2000CC';
    if (ccNum <= 2500) return '2001CC-2500CC';
    if (ccNum <= 3000) return '2501CC-3000CC';
    return '3001CC_ABOVE';
  };

  const getSeatRange = (seats) => {
    if (!seats) return '';
    const seatsNum = parseInt(seats);
    if (seatsNum == 1) return '1';
    if (seatsNum == 2) return '2';
    if (seatsNum == 3) return '3';
    if (seatsNum == 4) return '4';
    if (seatsNum == 5) return '5';
    if (seatsNum == 6) return '6';
    if (seatsNum == 7) return '7';
    return '8_ABOVE';
  };

  const getMileageRange = (mileage) => {
    if (!mileage) return '';
    const mileageNum = parseInt(mileage);
    if (mileageNum <= 5000) return '0KM-5000KM';
    if (mileageNum <= 10000) return '5001KM-10000KM';
    if (mileageNum <= 20000) return '10001KM-20000KM';
    if (mileageNum <= 30000) return '20001KM-30000KM';
    if (mileageNum <= 40000) return '30001KM-40000KM';
    if (mileageNum <= 50000) return '40001KM-50000KM';
    return '50001KM_ABOVE';
  };

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    carCategories: carModelId?.categoriesName || '',
    carBrand: carModelId?.carBrandName || '',
    carModelName: carModelId?.carModelName || '',
    carPriceRange: carModelId?.carPricerange || '',
    carPrice: carModelId?.carPriceValue || 0,
    carYear: carModelId?.year || 2022,
    carReferenceNumber: carModelId?.carRefNumber || 0,
    carRegistrationDate: carModelId?.carRegistrationDate || '',
    carManufactureDate: carModelId?.carManufactureDate || '',
    carModelCode: carModelId?.carModelCode || '',
    carGrade: carModelId?.carGrade || '',
    carChassisNumber: carModelId?.carChassisNumber || '',
    carEngineCCRange: carModelId?.carEngineRange || '',
    carEngineCC: carModelId?.carEngineValue || 0,
    carEngineCode: carModelId?.carEngineCode || '',
    carFuelType: carModelId?.carFuelType || '',
    carTransmission: carModelId?.carTransmission || '',
    carSteering: carModelId?.carSteering || '',
    carSeatRange: carModelId?.carSeatRange || '',
    carSeats: carModelId?.carSeatValue || 0,
    carDoors: carModelId?.carDoors || 0,
    carMileageRange: carModelId?.carMileageRange || '',
    carMileage: carModelId?.carMileageValue || 0,
    carColor: carModelId?.carColor || '',
    carDrive: carModelId?.carDriveType || '',
    carLocation: carModelId?.carLocation || '',
    carLength: carModelId?.carLength || 0,
    carHeight: carModelId?.carHeight || 0,
    carWidth: carModelId?.carWidth || 0,
    ourPick: carModelId?.ourPick || false,
    specialDeal: carModelId?.specialDeal || false,
    airConditioning: carModelId?.airConditioning || '',
    moreAccessories: carModelId?.moreAccessories || [],
    modelDescription: carModelId?.modelDescription || '',
    weight: carModelId?.carWeight || 0,
    vehicleMainAccessories: carModelId?.filterAccessories || [],
    shippingFeeConfig: carModelId?.shippingFeeConfig || [],
  });

  const handleShippingFeeChange = (optionIndex, feeIndex, value) => {
    setFormData(prev => {
      const updatedConfig = [...prev.shippingFeeConfig];

      if (!updatedConfig[optionIndex]) return prev;
      if (!updatedConfig[optionIndex].feeConfigs) return prev;

      const updatedFees = [...updatedConfig[optionIndex].feeConfigs];

      updatedFees[feeIndex] = {
        ...updatedFees[feeIndex],
        feeConfigAmount: Number(value) || 0
      };

      updatedConfig[optionIndex] = {
        ...updatedConfig[optionIndex],
        feeConfigs: updatedFees
      };

      return {
        ...prev,
        shippingFeeConfig: updatedConfig
      };
    });
  };

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      carPriceRange: getPriceRange(prev.carPrice),
      carEngineCCRange: getEngineCCRange(prev.carEngineCC),
      carSeatRange: getSeatRange(prev.carSeats),
      carMileageRange: getMileageRange(prev.carMileage)
    }));
  }, [formData.carPrice, formData.carEngineCC, formData.carSeats, formData.carMileage]);

  const [previewImages, setPreviewImages] = useState({
    exterior: Array(16).fill(null),
    interior: Array(15).fill(null),
    blueprint: null
  });

  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    type: '',
    index: null,
    imageUrl: null
  });

  const [mainAccessories, setMainAccessories] = useState([]);
  const [additionalAccessories, setAdditionalAccessories] = useState([]);
  const [newAccessory, setNewAccessory] = useState('');
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMainAccessoryChange = (accessory, isChecked) => {
    setFormData(prev => {
      const updatedAccessories = isChecked
        ? [...prev.vehicleMainAccessories, accessory]
        : prev.vehicleMainAccessories.filter(item => item !== accessory);
      return {
        ...prev,
        vehicleMainAccessories: updatedAccessories
      };
    });
  };

  const handleAdditionalAccessoryChange = (accessory, isChecked) => {
    setFormData(prev => {
      const updatedAccessories = isChecked
        ? [...prev.moreAccessories, accessory]
        : prev.moreAccessories.filter(item => item !== accessory);
      return {
        ...prev,
        moreAccessories: updatedAccessories
      };
    });
  };

  const handleSaveChanges = async () => {
    const firstInvalidField = validateForm();

    if (firstInvalidField) {
      const invalidElement = document.querySelector(`[name="${firstInvalidField}"]`);
      if (invalidElement) {
        invalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        invalidElement.focus();

        invalidElement.classList.add('highlight-error');
        setTimeout(() => {
          invalidElement.classList.remove('highlight-error');
        }, 2000);
      }
      return;
    }

    setIsSaving(true);
    try {
      const requestData = { ...formData };
      if (parseFloat(requestData.carPrice) === parseFloat(originalPrice)) {
        delete requestData.carPrice;
      }
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/admin-car-panel/update-car-model/${carModelId.carModelId}`,
        {
          method: 'PUT',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        }
      );

      const data = await response.json();

      if (data.success) {
        showDialog('Success', 'Car model updated successfully!');
        onClose();
        if (refreshCarList) {
          refreshCarList();
        }
      } else {
        showDialog('Error', data.message || 'Failed to update car model');
      }
    } catch (error) {
      console.error('Error updating car model:', error);
      showDialog('Error', 'Error updating car model');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAccessory = () => {
    if (newAccessory.trim()) {
      setShowConfirmationDialog(true);
    }
  };

  const confirmAddAccessory = async () => {
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/car-more-accessories/save?moreAccessoriesName=${encodeURIComponent(newAccessory)}`,
        {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        const accessoriesResponse = await fetch(
          'https://car.amar.co.jp/api/dashboard/product/get-all-accessories',
          {
            headers: {
              'accept': 'application/json',
              'Accept-Language': 'en'
            }
          }
        );
        const accessoriesData = await accessoriesResponse.json();
        setAllAccessories(accessoriesData);

        setNewAccessory('');
      } else {
        console.error('Failed to save accessory:', data.message);
      }
    } catch (error) {
      console.error('Error saving accessory:', error);
    } finally {
      setShowConfirmationDialog(false);
    }
  };

  const cancelAddAccessory = () => {
    setShowConfirmationDialog(false);
  };

  useEffect(() => {
    if (carModelId) {
      fetchImages();
    }
  }, [carModelId]);

  const fetchImages = async () => {
    try {
      const exteriorPromises = Array.from({ length: 15 }, (_, i) => i + 1).map(num =>
        fetch(`https://car.amar.co.jp/api/image/car-exterior-images-${num}/get-image/${carModelId.carModelId}`, {
          headers: {
            'accept': '*/*',
            'Accept-Language': 'en'
          }
        })
          .then(res => res.status === 200 ? res.blob() : null)
          .catch(() => null)
      );

      const interiorPromises = Array.from({ length: 15 }, (_, i) => i + 1).map(num =>
        fetch(`https://car.amar.co.jp/api/image/car-interior-images-${num}/get-image/${carModelId.carModelId}`, {
          headers: {
            'accept': '*/*',
            'Accept-Language': 'en'
          }
        })
          .then(res => res.status === 200 ? res.blob() : null)
          .catch(() => null)
      );

      const blueprintPromise = fetch(
        `https://car.amar.co.jp/api/image/car-exterior-images-16/get-image/${carModelId.carModelId}`,
        {
          headers: {
            'accept': '*/*',
            'Accept-Language': 'en'
          }
        }
      )
        .then(res => res.status === 200 ? res.blob() : null)
        .catch(() => null);

      Promise.all([...exteriorPromises, ...interiorPromises, blueprintPromise]).then(results => {
        const exteriorImages = results.slice(0, 15).map(blob => blob ? URL.createObjectURL(blob) : null);
        const interiorImages = results.slice(15, 30).map(blob => blob ? URL.createObjectURL(blob) : null);
        const blueprintImage = results[30] ? URL.createObjectURL(results[30]) : null;

        setPreviewImages({
          exterior: [...exteriorImages, null],
          interior: interiorImages,
          blueprint: blueprintImage
        });
      });

      if (allAccessories) {
        const main = allAccessories.filterAccessories.map(item => item.carFilterAccessories);
        const additional = allAccessories.moreAccessories.map(item => item.carMoreAccessories);
        setMainAccessories(main);
        setAdditionalAccessories(additional);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleImageUpload = async (type, index, file) => {
    if (!file) return;

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      let endpoint = '';
      if (type === 'exterior') {
        endpoint = `https://car.amar.co.jp/api/image/car-exterior-images-${index + 1}/upload-image/${carModelId.carModelId}`;
      } else if (type === 'interior') {
        endpoint = `https://car.amar.co.jp/api/image/car-interior-images-${index + 1}/upload-image/${carModelId.carModelId}`;
      } else if (type === 'blueprint') {
        endpoint = `https://car.amar.co.jp/api/image/car-exterior-images-16/upload-image/${carModelId.carModelId}`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'accept': '*/*',
          'Accept-Language': 'en'
        },
        body: formDataUpload
      });

      if (response.ok) {
        showDialog('Image uploaded successfully!');
        fetchImages();
      } else {
        showDialog('Error', 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      showDialog('Error', 'Error uploading image');
    }
  };

  const handleDeleteImage = async () => {
    const { type, index, imageUrl } = deleteConfirmation;

    try {
      let endpoint = '';
      if (type === 'exterior') {
        endpoint = `https://car.amar.co.jp/api/image/car-exterior-images-${index + 1}/delete-image/${carModelId.carModelId}`;
      } else if (type === 'interior') {
        endpoint = `https://car.amar.co.jp/api/image/car-interior-images-${index + 1}/delete-image/${carModelId.carModelId}`;
      } else if (type === 'blueprint') {
        endpoint = `https://car.amar.co.jp/api/image/car-exterior-images-16/delete-image/${carModelId.carModelId}`;
      }

      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Accept-Language': 'en'
        }
      });

      if (response.ok) {
        showDialog('Error', 'Image deleted successfully!');
        if (type === 'exterior') {
          setPreviewImages(prev => ({
            ...prev,
            exterior: prev.exterior.map((img, i) => i === index ? null : img)
          }));
        } else if (type === 'interior') {
          setPreviewImages(prev => ({
            ...prev,
            interior: prev.interior.map((img, i) => i === index ? null : img)
          }));
        } else if (type === 'blueprint') {
          setPreviewImages(prev => ({
            ...prev,
            blueprint: null
          }));
        }
      } else {
        showDialog('Error', 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      showDialog('Error', 'Error deleting image');
    } finally {
      setDeleteConfirmation({ show: false, type: '', index: null, imageUrl: null });
    }
  };

  const handleFileChange = (type, index, e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(type, index, file);
    }
    e.target.value = '';
  };

  const handleDrop = (type, index, e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageUpload(type, index, file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const showDeleteConfirmation = (type, index, imageUrl) => {
    setDeleteConfirmation({
      show: true,
      type,
      index,
      imageUrl
    });
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ show: false, type: '', index: null, imageUrl: null });
  };

  const renderImageUploadSection = (type, count, title) => {
    const images = type === 'exterior' ? previewImages.exterior.slice(0, 15) : previewImages.interior;

    return (
      <div className="form-section-all-car-listed">
        <h3 className="h3-all-car-listed">{title}</h3>
        <div className="image-upload-grid">
          {Array.from({ length: count }).map((_, index) => {
            const displayUrl = images[index];

            return (
              <div
                key={`${type}-${index}`}
                className="image-upload-item"
                onDrop={(e) => handleDrop(type, index, e)}
                onDragOver={handleDragOver}
              >
                <div className="image-preview-container">
                  {displayUrl ? (
                    <>
                      <img
                        src={displayUrl}
                        alt={`${title} ${index + 1}`}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/carousel/carslider.png';
                        }}
                      />
                      <button
                        className="delete-image-btn-all-car-listed"
                        onClick={() => showDeleteConfirmation(type, index, displayUrl)}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <div className="empty-image-placeholder">
                      <span>No Image</span>
                    </div>
                  )}
                </div>
                <div className="image-upload-controls">
                  <label className="image-upload-label">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(type, index, e)}
                      style={{ display: 'none' }}
                    />
                    Upload Image
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderBlueprintSection = () => {
    return (
      <div className="form-section-all-car-listed">
        <h3 className="h3-all-car-listed">Blue Print</h3>
        <div className="image-upload-grid">
          <div
            className="image-upload-item"
            onDrop={(e) => handleDrop('blueprint', 0, e)}
            onDragOver={handleDragOver}
          >
            <div className="image-preview-container">
              {previewImages.blueprint ? (
                <>
                  <img
                    src={previewImages.blueprint}
                    alt="Blue Print"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/carousel/carslider.png';
                    }}
                  />
                  <button
                    className="delete-image-btn-all-car-listed"
                    onClick={() => showDeleteConfirmation('blueprint', 0, previewImages.blueprint)}
                  >
                    Delete
                  </button>
                </>
              ) : (
                <div className="empty-image-placeholder">
                  <span>No Blue Print Image</span>
                </div>
              )}
            </div>
            <div className="image-upload-controls">
              <label className="image-upload-label">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange('blueprint', 0, e)}
                  style={{ display: 'none' }}
                />
                Upload Blue Print
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="full-view-edit-modal">
      {dialog.show && (
        <div className="dialog-overlay">
          <div className="dialog">
            <h3>{dialog.title}</h3>
            <p>{dialog.message}</p>
            <div className="dialog-buttons">
              {dialog.onCancel && (
                <button className="dialog-button cancel" onClick={() => {
                  dialog.onCancel?.();
                  setDialog(prev => ({ ...prev, show: false }));
                }}>
                  Cancel
                </button>
              )}
              <button className="dialog-button confirm" onClick={() => {
                dialog.onConfirm?.();
                setDialog(prev => ({ ...prev, show: false }));
              }}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="responsive-modal-header">
        <h2>Edit Car Model</h2>
        <button className="close-modal-btn-all-car-listed" onClick={onClose}>
          <svg viewBox="0 0 24 24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      </div>

      <div className="fullscreen-modal-content">
        <div className="form-section-all-car-listed">
          <h3 className="h3-all-car-listed">Basic Information</h3>
          <div className="form-grid">
            <div className="form-group-all-car-listed">
              <label>Category</label>
              <select
                name="carCategories"
                value={formData.carCategories || ''}
                onChange={handleInputChange}
                onFocus={fetchCategories}
                className={errors.carCategories ? 'error-field' : ''}
                disabled
              >
                <option value="">Select Category</option>
                {loadingCategories ? (
                  <option value="" disabled>Loading...</option>
                ) : (
                  categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))
                )}
              </select>
              {errors.carCategories && <span className="error-message">{errors.carCategories}</span>}
            </div>
            <div className="form-group-all-car-listed">
              <label>Car Brand</label>
              <select
                name="carBrand"
                value={formData.carBrand || ''}
                onChange={handleInputChange}
                onFocus={fetchBrands}
                className={errors.carBrand ? 'error-field' : ''}
                disabled
              >
                <option value="">Select Brand</option>
                {loadingBrands ? (
                  <option value="" disabled>Loading...</option>
                ) : (
                  brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))
                )}
              </select>
              {errors.carBrand && <span className="error-message">{errors.carBrand}</span>}
            </div>
            <div className="form-group-all-car-listed">
              <label>Car Model Name</label>
              <input
                type="text"
                name="carModelName"
                value={formData.carModelName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Price</label>
              <input
                type="number"
                name="carPrice"
                value={formData.carPrice}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Price Range</label>
              <select
                name="carPriceRange"
                value={formData.carPriceRange}
                onChange={handleInputChange}
                disabled
              >
                <option value="">Select Price Range</option>
                {priceRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>
            <div className="form-group-all-car-listed">
              <label>Year</label>
              <input
                type="number"
                name="carYear"
                value={formData.carYear}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section-all-car-listed">
          <h3 className="h3-all-car-listed">Identification Details</h3>
          <div className="form-grid">
            <div className="form-group-all-car-listed">
              <label>Reference Number</label>
              <input
                type="text"
                name="carReferenceNumber"
                value={formData.carReferenceNumber}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Registration Date</label>
              <input
                type="date"
                name="carRegistrationDate"
                value={formData.carRegistrationDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Manufacture Date</label>
              <input
                type="date"
                name="carManufactureDate"
                value={formData.carManufactureDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Model Code</label>
              <input
                type="text"
                name="carModelCode"
                value={formData.carModelCode}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Grade</label>
              <input
                type="text"
                name="carGrade"
                value={formData.carGrade}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Chassis Number</label>
              <input
                type="text"
                name="carChassisNumber"
                value={formData.carChassisNumber}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section-all-car-listed">
          <h3 className="h3-all-car-listed">Engine & Transmission</h3>
          <div className="form-grid">
            <div className="form-group-all-car-listed">
              <label>Engine CC</label>
              <input
                type="number"
                name="carEngineCC"
                value={formData.carEngineCC}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Engine Range</label>
              <select
                name="carEngineCCRange"
                value={formData.carEngineCCRange}
                onChange={handleInputChange}
                disabled
              >
                <option value="">Select Engine Range</option>
                {engineRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>

            <div className="form-group-all-car-listed">
              <label>Engine Code</label>
              <input
                type="text"
                name="carEngineCode"
                value={formData.carEngineCode}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Fuel Type</label>
              <select
                name="carFuelType"
                value={formData.carFuelType}
                onChange={handleInputChange}
                onFocus={fetchFuelTypes}
              >
                <option value="">Select Fuel Type</option>
                {loadingFuelTypes ? (
                  <option value="" disabled>Loading...</option>
                ) : (
                  fuelTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))
                )}
              </select>
            </div>
            <div className="form-group-all-car-listed">
              <label>Transmission</label>
              <select
                name="carTransmission"
                value={formData.carTransmission}
                onChange={handleInputChange}
                onFocus={fetchTransmissions}
              >
                <option value="">Select Transmission</option>
                {loadingTransmissions ? (
                  <option value="" disabled>Loading...</option>
                ) : (
                  transmissions.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))
                )}
              </select>
            </div>

            <div className="form-group-all-car-listed">
              <label>Steering</label>
              <select
                name="carSteering"
                value={formData.carSteering}
                onChange={handleInputChange}
                onFocus={fetchSteeringOptions}
              >
                <option value="">Select Steering</option>
                {loadingSteering ? (
                  <option value="" disabled>Loading...</option>
                ) : (
                  steeringOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))
                )}
              </select>
            </div>
          </div>
        </div>

        <div className="form-section-all-car-listed">
          <h3 className="h3-all-car-listed">Dimensions & Capacity</h3>
          <div className="form-grid">
            <div className="form-group-all-car-listed">
              <label>Seats</label>
              <input
                type="number"
                name="carSeats"
                value={formData.carSeats}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Seat Range</label>
              <select
                name="carSeatRange"
                value={formData.carSeatRange}
                onChange={handleInputChange}
                disabled
              >
                <option value="">Select Seat Range</option>
                {seatRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>
            <div className="form-group-all-car-listed">
              <label>Doors</label>
              <input
                type="number"
                name="carDoors"
                value={formData.carDoors}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Mileage</label>
              <input
                type="number"
                name="carMileage"
                value={formData.carMileage}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Mileage Range</label>
              <select
                name="carMileageRange"
                value={formData.carMileageRange}
                onChange={handleInputChange}
                disabled
              >
                <option value="">Select Mileage Range</option>
                {mileageRanges.map(range => (
                  <option key={range} value={range}>{range}</option>
                ))}
              </select>
            </div>

            <div className="form-group-all-car-listed">
              <label>Weight (kg)</label>
              <input
                type="number"
                name="weight"
                value={formData.weight}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section-all-car-listed">
          <h3 className="h3-all-car-listed">Appearance & Features</h3>
          <div className="form-grid">
            <div className="form-group-all-car-listed">
              <label>Color</label>
              <select
                name="carColor"
                value={formData.carColor}
                onChange={handleInputChange}
                onFocus={fetchColors}
              >
                <option value="">Select Color</option>
                {loadingColors ? (
                  <option value="" disabled>Loading...</option>
                ) : (
                  colors.map(color => (
                    <option key={color} value={color}>{color}</option>
                  ))
                )}
              </select>
            </div>

            <div className="form-group-all-car-listed">
              <label>Drive Type</label>
              <select
                name="carDrive"
                value={formData.carDrive}
                onChange={handleInputChange}
              >
                <option value="">Select Drive Type</option>
                {driveTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="form-group-all-car-listed">
              <label>Location</label>
              <input
                type="text"
                name="carLocation"
                value={formData.carLocation}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Length (mm)</label>
              <input
                type="number"
                name="carLength"
                value={formData.carLength}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Width (mm)</label>
              <input
                type="number"
                name="carWidth"
                value={formData.carWidth}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Height (mm)</label>
              <input
                type="number"
                name="carHeight"
                value={formData.carHeight}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed">
              <label>Air Conditioning</label>
              <input
                type="text"
                name="airConditioning"
                value={formData.airConditioning}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group-all-car-listed checkbox-group inline-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="ourPick"
                  checked={formData.ourPick}
                  onChange={handleInputChange}
                />
                Our Pick
              </label>
            </div>
            <div className="form-group-all-car-listed checkbox-group inline-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="specialDeal"
                  checked={formData.specialDeal}
                  onChange={handleInputChange}
                />
                Special Deal
              </label>
            </div>
          </div>
        </div>

        <div className="form-section-all-car-listed">
          <h3 className="h3-all-car-listed">Shipping Options & Fees</h3>

          {loadingPortFeeConfigs ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "150px",
                flexDirection: "column"
              }}
            >
              <div className="loading-spinner" />
              <p>Loading shipping options.</p>
            </Box>
          ) : (
            portFeeConfigs?.length > 0 && (
              <div className="shipping-options-matrix">
                <div className="shipping-fee-grid-matrix">
                  <div style={{
                    padding: '10px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    color: '#d32f2f'
                  }}>
                    Fee Type
                  </div>

                  {portFeeConfigs.map(cfg => (
                    <div key={cfg.shippingOptionName} className="matrix-header">
                      {cfg.shippingOptionName} Fee Configuration
                    </div>
                  ))}

                  {getAllUniqueFees().map(feeType => (
                    <React.Fragment key={feeType}>
                      <div className="fee-label-cell">{formatFeeLabel(feeType)} ($)</div>

                      {portFeeConfigs.map(cfg => {
                        const hasThisFee = (cfg.feeConfigurationList || [])
                          .map(f => f.trim())
                          .includes(feeType);

                        if (!hasThisFee) {
                          return (
                            <div
                              key={`${cfg.shippingOptionName}-${feeType}-na`}
                              className="na-cell"
                            >
                              N/A
                            </div>
                          );
                        }

                        const { optionIndex, feeIndex } = getIndices(
                          cfg.shippingOptionName,
                          feeType
                        );

                        const value =
                          optionIndex > -1 && feeIndex > -1
                            ? formData.shippingFeeConfig[optionIndex].feeConfigs[feeIndex]
                              .feeConfigAmount ?? ''
                            : '';

                        return (
                          <input
                            key={`${cfg.shippingOptionName}-${feeType}-input`}
                            type="number"
                            min="0"
                            step="0.01"
                            className="ship-input"
                            value={value}
                            onChange={e =>
                              handleShippingFeeChange(optionIndex, feeIndex, e.target.value)
                            }
                            placeholder="0.00"
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )
          )}
        </div>

        <div className="form-section-all-car-listed">
          <h3 className="h3-all-car-listed">Main Accessories</h3>
          <div className="form-group-all-car-listed">
            <label>Select Vehicle Main Accessories</label>
            <div className="accessories-toggle-grid">
              {Array.isArray(allAccessories?.filterAccessories) ? (
                allAccessories.filterAccessories.map((accessory) => (
                  <div key={accessory.carFilterAccessoriesId} className="accessory-toggle-item">
                    <span className="accessory-label">{accessory.carFilterAccessories}</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={formData.vehicleMainAccessories.includes(accessory.carFilterAccessories)}
                        onChange={(e) => handleMainAccessoryChange(accessory.carFilterAccessories, e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-yes">Yes</span>
                      <span className="toggle-no">No</span>
                    </label>
                  </div>
                ))
              ) : (
                <p>No main accessories available</p>
              )}
            </div>
          </div>
        </div>

        <div className="form-section-all-car-listed">
          <h3 className="h3-all-car-listed">Additional Accessories</h3>
          <div className="form-group-all-car-listed">
            <div className="accessories-toggle-grid">
              {Array.isArray(allAccessories?.moreAccessories) ? (
                allAccessories.moreAccessories.map((item) => (
                  <div key={item.carMoreAccessoriesId} className="accessory-toggle-item">
                    <span className="accessory-label">{item.carMoreAccessories}</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={formData.moreAccessories.includes(item.carMoreAccessories)}
                        onChange={(e) => handleAdditionalAccessoryChange(item.carMoreAccessories, e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-yes">Yes</span>
                      <span className="toggle-no">No</span>
                    </label>
                  </div>
                ))
              ) : (
                <p>No additional accessories available</p>
              )}
            </div>
          </div>

          <div className="form-group-all-car-listed">
            <div className="add-accessory-input">
              <input
                type="text"
                placeholder="Enter new accessory"
                value={newAccessory}
                onChange={(e) => setNewAccessory(e.target.value)}
              />
              <button
                className="add-accessory-btn-all-car-listed"
                onClick={handleAddAccessory}
                disabled={!newAccessory.trim()}
              >
                Add
              </button>
            </div>
          </div>
          <br />
          {showConfirmationDialog && (
            <div className="confirmation-dialog-overlay">
              <div className="confirmation-dialog">
                <h3>Confirm Addition</h3>
                <p>Are you sure you want to add "{newAccessory}" as a new accessory?</p>
                <div className="confirmation-buttons">
                  <button
                    className="cancel-btn-all-car-listed"
                    onClick={cancelAddAccessory}
                  >
                    Cancel
                  </button>
                  <button
                    className="confirm-btn-all-car-listed"
                    onClick={confirmAddAccessory}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="form-section-all-car-listed">
            <h3 className="h3-all-car-listed">Description</h3>
            <div className="form-group-all-car-listed">
              <label>Model Description</label>
              <textarea
                name="modelDescription"
                value={formData.modelDescription}
                onChange={handleInputChange}
                rows={5}
                placeholder="Enter detailed description about the car model..."
              />
            </div>
          </div>
        </div>

        {renderImageUploadSection('exterior', 15, 'Exterior Images')}
        {renderImageUploadSection('interior', 15, 'Interior Images')}
        {renderBlueprintSection()}

        <div className="edit-modal-buttons">
          <button
            className="save-btn-all-car-listed"
            onClick={handleSaveChanges}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
          <button className="cancel-btn-all-car-listed" onClick={onClose}>Cancel</button>
        </div>
      </div>

      {deleteConfirmation.show && (
        <div className="confirmation-dialog-overlay">
          <div className="confirmation-dialog">
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this image?</p>
            <div className="confirmation-buttons">
              <button
                className="cancel-btn-all-car-listed"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                className="confirm-btn-all-car-listed"
                onClick={handleDeleteImage}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const AllCarListed = () => {
  // ============ SEARCH FUNCTIONALITY STATES ============
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionTimeoutRef = useRef(null);
  // ================================================

  const [newAccessoryInput, setNewAccessoryInput] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [carToDelete, setCarToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [currentView, setCurrentView] = useState('available');

  const navigate = useNavigate();

  const priceRanges = [
    '$0-$1000', '$1001-$2000', '$2001-$3000', '$3001-$4000', '$4001-$5000',
    '$5001-$6000', '$6001-$7000', '$7001-$8000', '$8001-$9000', '$9001-$10000', '$10001_ABOVE'
  ];

  const engineRanges = [
    '0CC-500CC', '501CC-1000CC', '1001CC-1500CC', '1501CC-2000CC',
    '2001CC-2500CC', '2501CC-3000CC', '3001CC_ABOVE'
  ];

  const seatRanges = ['1', '2', '3', '4', '5', '6', '7', '8_ABOVE'];
  const mileageRanges = [
    '0KM-5000KM', '5001KM-10000KM', '10001KM-20000KM', '20001KM-30000KM',
    '30001KM-40000KM', '40001KM-50000KM', '50001KM_ABOVE'
  ];

  const driveTypes = ['2WD', '4WD', 'AWD', 'FWD', 'RWD'];

  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [allAccessories, setAllAccessories] = useState(null);

  const isInitialMount = useRef(true);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const handlePageChange = (event, page) => {
    const zeroBasedPage = page - 1;
    if (zeroBasedPage >= 0 && zeroBasedPage < totalPages) {
      setCurrentPage(zeroBasedPage);
    }
  };

  const handleItemsPerPageChange = (event) => {
    const newItemsPerPage = event.target.value;
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0);
  };

  // ============ SEARCH FUNCTIONALITY FUNCTIONS ============

  // Fetch suggestions from search API - UPDATED WITH NEW ENDPOINT
  const fetchSuggestions = async (query) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/global/search/filter/admin-panel?inputType=${encodeURIComponent(query)}&page=0&size=10`,
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch suggestions');

      const data = await response.json();
      if (data.data && Array.isArray(data.data)) {
        setSuggestions(data.data);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Handle search input change with debouncing
  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }

    if (value.trim()) {
      setIsLoadingSuggestions(true);
      suggestionTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (car) => {
    setSelectedCar(car);
    setIsViewModalOpen(true);
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchTerm('');
  };

  // Fetch search results for the table - UPDATED WITH NEW ENDPOINT
  const fetchSearchResults = async (query, page = 0, size = itemsPerPage) => {
    setIsTableLoading(true);

    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/dashboard/global/search/filter/admin-panel?inputType=${encodeURIComponent(query)}&page=${page}&size=${size}`,
        {
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch search results');

      const data = await response.json();
      if (data.data && Array.isArray(data.data)) {
        setCars(data.data);
        if (data.pagination || data.totalPages) {
          setTotalPages(data.totalPages || 1);
          setTotalElements(data.totalItems || data.data.length);
        } else {
          setTotalPages(1);
          setTotalElements(data.data.length);
        }
      } else {
        setCars([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
      setCars([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setIsTableLoading(false);
    }
  };

  // Handle search button click
  const handleSearchClick = () => {
    if (!searchTerm.trim()) {
      setCars([]);
      setIsSearchMode(false);
      return;
    }

    setIsSearchMode(true);
    setCurrentPage(0);
    fetchSearchResults(searchTerm, 0);
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
      setShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (suggestionTimeoutRef.current) {
        clearTimeout(suggestionTimeoutRef.current);
      }
    };
  }, []);

  // ================================================

  // Enhanced fetch function to handle different endpoints
  const fetchData = async (view, page = currentPage, size = itemsPerPage) => {
    setIsTableLoading(true);

    let endpoint = '';
    if (view === 'all') {
      endpoint = `${BASE_URL}/api/authorization/admin-dashboard-panel/get-list-of-car`;
    } else if (view === 'available') {
      endpoint = `${BASE_URL}/api/authorization/admin-dashboard-panel/get-list-of-available-car`;
    } else if (view === 'sold') {
      endpoint = `${BASE_URL}/api/authorization/admin-dashboard-panel/get-list-of-sold-car`;
    }

    try {
      const url = new URL(endpoint);
      url.searchParams.append('page', page);
      url.searchParams.append('size', size);

      const response = await fetch(url.toString(), {
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en'
        }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      if (data && data.data && Array.isArray(data.data)) {
        setCars(data.data);
        if (data.pagination) {
          setTotalPages(data.pagination.totalPages);
          setTotalElements(data.pagination.totalElements);
        } else {
          setTotalPages(1);
          setTotalElements(data.data.length);
        }
      } else {
        setCars([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error(`Error fetching ${view} cars:`, error);
      setCars([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setIsTableLoading(false);
      if (loading) {
        setLoading(false);
      }
    }
  };

  // Delete car function
  const handleDeleteCar = async () => {
    if (!carToDelete) return;

    setIsDeleting(true);

    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/admin-car-panel/delete-car-model/${carToDelete.carModelId}`, {
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
        }
      });

      if (response.ok) {
        if (isSearchMode) {
          fetchSearchResults(searchTerm, currentPage);
        } else {
          fetchData(currentView, currentPage);
        }
        setIsDeletePopupOpen(false);
        setCarToDelete(null);
      } else {
        console.error('Failed to delete car');
        alert('Failed to delete car. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting car:', error);
      alert('Error occurred while deleting car.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle delete button click
  const handleDeleteClick = (car) => {
    setCarToDelete(car);
    setIsDeletePopupOpen(true);
  };

  // Close delete popup
  const handleCloseDeletePopup = () => {
    setIsDeletePopupOpen(false);
    setCarToDelete(null);
  };

  // Helper function for All Cars tab
  const switchToAllCars = () => {
    if (currentView !== 'all' || isSearchMode) {
      setCurrentView('all');
      setCurrentPage(0);
      setIsSearchMode(false);
      setSearchTerm('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Helper function for Available Cars tab
  const switchToAvailableCars = () => {
    if (currentView !== 'available' || isSearchMode) {
      setCurrentView('available');
      setCurrentPage(0);
      setIsSearchMode(false);
      setSearchTerm('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Helper function for Sold Cars tab
  const switchToSoldCars = () => {
    if (currentView !== 'sold' || isSearchMode) {
      setCurrentView('sold');
      setCurrentPage(0);
      setIsSearchMode(false);
      setSearchTerm('');
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Optimized data fetch effect
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!isSearchMode) {
        fetchData(currentView, currentPage, itemsPerPage);
      }
    } else {
      if (!isSearchMode) {
        fetchData(currentView, currentPage, itemsPerPage);
      } else {
        fetchSearchResults(searchTerm, currentPage, itemsPerPage);
      }
    }
  }, [currentPage, itemsPerPage, currentView, isSearchMode]);

  // Calculate display range
  const startIndex = (currentPage * itemsPerPage) + 1;
  const endIndex = Math.min(startIndex + cars.length - 1, totalElements);
  const displayPage = currentPage + 1;

  // Check if mobile
  const isMobile = window.innerWidth < 768;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2
        }}
      >
        <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
        <Typography variant="body1">Loading car data...</Typography>
      </Box>
    );
  }

  return (
    <>
      <div className="cars-dashboard">
        {isDeletePopupOpen && carToDelete && (
          <div className="popup-overlay">
            <div className="popup-content">
              <div className="popup-header">
                <h3>Confirm Delete</h3>
                <button
                  className="close-btn-all-car-listed"
                  onClick={handleCloseDeletePopup}
                >
                  ×
                </button>
              </div>

              <div className="popup-body">
                <p>Are you sure you want to delete <strong>{carToDelete.carBrandName} {carToDelete.carModelName}</strong>?</p>
              </div>

              <div className="popup-footer">
                <button
                  className="cancel-btn-all-car-listed"
                  onClick={handleCloseDeletePopup}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="delete-confirm-btn-all-car-listed"
                  onClick={handleDeleteCar}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {isViewModalOpen && selectedCar && (
          <div className="modal-overlay">
            <div className="view-modal">
              <div className="view-modal-header">
                <h2>{selectedCar.carBrandName} {selectedCar.carModelName} Details</h2>
                <button
                  className="close-modal-btn-all-car-listed"
                  onClick={() => setIsViewModalOpen(false)}
                >
                  <svg viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </div>

              <div className="view-modal-content">
                <div className="car-image-section">
                  <img
                    src={`https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${selectedCar.carModelId}`}
                    alt={`${selectedCar.carBrandName} ${selectedCar.carModelName}`}
                    onClick={() => navigate(`/product/${selectedCar.carModelId}`)}
                    style={{ cursor: 'pointer' }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/carousel/carslider.png';
                    }}
                  />
                  <div className="car-basic-info">
                    <h3>{selectedCar.carBrandName} {selectedCar.carModelName}</h3>
                    <div className="car-meta">
                      <span>{selectedCar.year}</span>
                      <span>{selectedCar.carFuelType}</span>
                      <span>{selectedCar.carTransmission}</span>
                      <span>{selectedCar.carMileageValue?.toLocaleString()} KM</span>
                    </div>
                    <div className="car-price">
                      {formatPrice(selectedCar.carPriceValue)}
                    </div>
                  </div>
                </div>

                <div className="details-sections">
                  <div className="details-section">
                    <h4 className="section-title">Basic Information</h4>
                    <div className="specs-grid">
                      <div className="spec-item">
                        <span className="spec-label">Reference Number</span>
                        <span className="spec-value">{selectedCar.carRefNumber || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Brand</span>
                        <span className="spec-value">{selectedCar.carBrandName}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Model</span>
                        <span className="spec-value">{selectedCar.carModelName}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Category</span>
                        <span className="spec-value">{selectedCar.categoriesName || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Grade</span>
                        <span className="spec-value">{selectedCar.carGrade || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Model Code</span>
                        <span className="spec-value">{selectedCar.carModelCode || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Year</span>
                        <span className="spec-value">{selectedCar.year}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Price</span>
                        <span className="spec-value">{formatPrice(selectedCar.carPriceValue)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="details-section">
                    <h4 className="section-title">Registration & Manufacture</h4>
                    <div className="specs-grid">
                      <div className="spec-item">
                        <span className="spec-label">Registration Date</span>
                        <span className="spec-value">{selectedCar.carRegistrationDate || selectedCar.registartionDate || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Manufacture Date</span>
                        <span className="spec-value">{selectedCar.carManufactureDate || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="details-section">
                    <h4 className="section-title">Technical Specifications</h4>
                    <div className="specs-grid">
                      <div className="spec-item">
                        <span className="spec-label">Chassis Number</span>
                        <span className="spec-value">{selectedCar.carChassisNumber || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Engine CC</span>
                        <span className="spec-value">{selectedCar.carEngineValue}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Engine Code</span>
                        <span className="spec-value">{selectedCar.carEngineCode || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Fuel Type</span>
                        <span className="spec-value">{selectedCar.carFuelType}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Transmission</span>
                        <span className="spec-value">{selectedCar.carTransmission}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Steering</span>
                        <span className="spec-value">{selectedCar.carSteering || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Seats</span>
                        <span className="spec-value">{selectedCar.carSeatValue}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Doors</span>
                        <span className="spec-value">{selectedCar.carDoors}</span>
                      </div>
                    </div>
                  </div>

                  <div className="details-section">
                    <h4 className="section-title">Vehicle Details</h4>
                    <div className="specs-grid">
                      <div className="spec-item">
                        <span className="spec-label">Color</span>
                        <span className="spec-value">{selectedCar.carColor || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Weight (kg)</span>
                        <span className="spec-value">{selectedCar.carWeight || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Drive Type</span>
                        <span className="spec-value">{selectedCar.carDriveType || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Air Conditioning</span>
                        <span className="spec-value">{selectedCar.airConditioning || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Location</span>
                        <span className="spec-value">{selectedCar.carLocation || 'N/A'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Our Pick</span>
                        <span className="spec-value">{selectedCar.ourPick ? 'Yes' : 'No'}</span>
                      </div>
                      <div className="spec-item">
                        <span className="spec-label">Special Deal</span>
                        <span className="spec-value">{selectedCar.specialDeal ? 'Yes' : 'No'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="details-section">
                    <h4 className="section-title">Description</h4>
                    <div className="specs-grid">
                      <div className="spec-value description-text">
                        {selectedCar.modelDescription || 'No description available'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showEditModal && selectedCar && (
          <EditCarModal
            carModelId={selectedCar}
            onClose={() => {
              setShowEditModal(false);
              setSelectedCar(null);
            }}
            priceRanges={priceRanges}
            engineRanges={engineRanges}
            fuelTypes={[]}
            transmissions={[]}
            steeringOptions={[]}
            seatRanges={seatRanges}
            mileageRanges={mileageRanges}
            colors={[]}
            driveTypes={driveTypes}
            allAccessories={allAccessories}
            setAllAccessories={setAllAccessories}
            refreshCarList={() => {
              if (isSearchMode) {
                fetchSearchResults(searchTerm, currentPage);
              } else {
                fetchData(currentView, currentPage);
              }
            }}
          />
        )}

        <div className="dashboard-header">
          <div className="header-content">
            <h1>Car Inventory</h1>
            <p>Manage all vehicles in your dealership</p>
          </div>
        </div>

        <div className="dashboard-controls">
          {/* ============ NEW SEARCH BAR WITH SUGGESTIONS ============ */}
          <SearchInputWrapper ref={searchInputRef} sx={{ flex: 1, maxWidth: "500px" }}>
            <TextField
              placeholder="Search cars by brand, model, or type..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchButton
                      onClick={handleSearchClick}
                      title="Search"
                    >
                      <SearchIcon style={{ color: 'red' }} />
                    </SearchButton>
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "3px",
                  background: "#fff",
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#ccc",
                  },
                  "&:hover fieldset": {
                    borderColor: "red",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "red",
                  },
                }
              }}
            />

            {/* Suggestions Dropdown */}
            {showSuggestions && (
              <SuggestionsDropdown>
                {isLoadingSuggestions ? (
                  <SuggestionsLoading>
                    <p>Loading suggestions...</p>
                  </SuggestionsLoading>
                ) : suggestions.length > 0 ? (
                  <SuggestionsList>
                    {suggestions.map((car) => (
                      <SuggestionItem
                        key={car.carModelId}
                        onClick={() => handleSuggestionSelect(car)}
                      >
                        <SuggestionImage>
                          <img
                            src={`https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${car.carModelId}`}
                            alt={`${car.carBrandName} ${car.carModelName}`}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/carousel/carslider.png';
                            }}
                          />
                        </SuggestionImage>
                        <SuggestionInfo>
                          <h4>{car.carBrandName} {car.carModelName}</h4>
                          <p>{car.year} • {car.carFuelType} • {formatPrice(car.carPriceValue)}</p>
                        </SuggestionInfo>
                      </SuggestionItem>
                    ))}
                  </SuggestionsList>
                ) : (
                  <SuggestionsEmpty>
                    <p>No cars found matching "{searchTerm}"</p>
                  </SuggestionsEmpty>
                )}
              </SuggestionsDropdown>
            )}
          </SearchInputWrapper>
          {/* ===================================================== */}

          <div className="results-count">
            {isSearchMode ? (
              `Showing ${startIndex}-${endIndex} of ${totalElements} search results`
            ) : currentView === 'sold' ? (
              `Showing ${startIndex}-${endIndex} of ${totalElements} Sold Vehicles`
            ) : currentView === 'available' ? (
              `Showing ${startIndex}-${endIndex} of ${totalElements} Available Vehicles`
            ) : (
              `Showing ${startIndex}-${endIndex} of ${totalElements} total Vehicles`
            )}
          </div>

          <div className="filter-tabs">


            <button
              className={`filter-tab ${currentView === 'available' && !isSearchMode ? 'active' : ''}`}
              onClick={switchToAvailableCars}
              disabled={isTableLoading}
            >
              Available
            </button>

            <button
              className={`filter-tab ${currentView === 'sold' && !isSearchMode ? 'active' : ''}`}
              onClick={switchToSoldCars}
              disabled={isTableLoading}
            >
              Sold
            </button>

            <button
              className={`filter-tab ${(currentView === 'all' && !isSearchMode) ? 'active' : ''}`}
              onClick={switchToAllCars}
              disabled={isTableLoading}
            >
              All Cars
            </button>

          </div>
        </div>

        <div className="cars-table-container">
          {isTableLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
                flexDirection: "column",
                gap: 2
              }}
            >
              <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
              <Typography variant="body1">Loading car data...</Typography>
            </Box>
          ) : cars.length > 0 ? (
            <div className="responsive-table">
              <table className="cars-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Make & Model</th>
                    <th>Year</th>
                    <th>Price</th>
                    <th>Mileage</th>
                    <th>Fuel Type</th>
                    <th>Transmission</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cars.map(car => (
                    <tr key={car.carModelId}>
                      <td data-label="Image">
                        <div className="car-image">
                          <img
                            src={`https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${car.carModelId}`}
                            alt={`${car.carBrandName} ${car.carModelName}`}
                            onClick={() => navigate(`/product/${car.carModelId}`)}
                            style={{ cursor: 'pointer' }}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/carousel/carslider.png';
                            }}
                          />
                        </div>
                      </td>
                      <td data-label="Make & Model">
                        <div className="car-make-model">
                          <strong>{car.carBrandName} {car.carModelName}</strong>
                        </div>
                      </td>
                      <td data-label="Year">{car.year}</td>
                      <td data-label="Price">{formatPrice(car.carPriceValue)}</td>
                      <td data-label="Mileage">{car.carMileageValue ? `${car.carMileageValue.toLocaleString()} KM` : 'N/A'}</td>
                      <td data-label="Fuel Type">{car.carFuelType}</td>
                      <td data-label="Transmission">{car.carTransmission}</td>
                      <td data-label="Actions" align='center'>
                        <div className="action-buttons-all-car-listed">
                          <button
                            className="action-btn-all-car-listed all-car-view-btn-all-car-listed"
                            title="View"
                            onClick={() => {
                              setSelectedCar(car);
                              setIsViewModalOpen(true);
                            }}
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                            </svg>
                          </button>
                          <button
                            className="action-btn-all-car-listed all-car-edit-btn-all-car-listed"
                            title="Edit"
                            onClick={() => {
                              setSelectedCar(car);
                              setShowEditModal(true);
                            }}
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                            </svg>
                          </button>
                          <button
                            className="action-btn-all-car-listed all-car-delete-btn-all-car-listed"
                            title="Delete"
                            onClick={() => handleDeleteClick(car)}
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-results">
              <svg viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
              </svg>
              <h3>No cars found</h3>
              <p>
                {isSearchMode
                  ? `No cars found matching "${searchTerm}".`
                  : currentView === 'sold'
                    ? 'No sold cars found.'
                    : currentView === 'available'
                      ? 'No available cars found.'
                      : 'No cars available in the inventory.'
                }
              </p>
            </div>
          )}
        </div>

        {totalElements > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              mb: 4,
              flexDirection: isMobile ? "column" : "row",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "black",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                }}
              >
                Results per page:{itemsPerPage}
              </Typography>
            </Box>

            <Pagination
              count={totalPages}
              page={displayPage}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
              sx={{
                display: "flex",
                justifyContent: "center",
                "& .MuiPaginationItem-root": {
                  fontWeight: 500,
                  borderColor: "black",
                  color: "black",
                  "&:hover": {
                    backgroundColor: "rgba(255, 0, 0, 0.1)",
                    borderColor: "red",
                  },
                },
                "& .MuiPaginationItem-ellipsis": {
                  border: "none",
                  "&:hover": {
                    backgroundColor: "transparent",
                  },
                },
                "& .Mui-selected": {
                  backgroundColor: "red !important",
                  color: "white",
                  borderColor: "red",
                  "&:hover": {
                    backgroundColor: "#b30000 !important",
                    borderColor: "#800000",
                  },
                },
              }}
            />
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              variant="outlined"
              displayEmpty
              size="small"
              sx={{
                width: 80,
                color: "black",
                fontWeight: 500,
                backgroundColor: "#fff",
                borderColor: "black",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "black",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "red",
                },
                "& .MuiSvgIcon-root": {
                  color: "red",
                },
              }}
            >
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Box>
        )}
      </div>
    </>
  );
};

export default AllCarListed;